import { useQueryClient } from '@tanstack/react-query'
import { ResetUsagePolicy } from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { qk, useProjects, useSettings, useUsage, useWorkstreams } from '../lib/queries.js'
import { accountWindows } from '../lib/usage-accounting.js'
import { errorText, useToast } from './ui.js'

export function UsageSettings() {
  const settings = useSettings()
  const usage = useUsage()
  const projects = useProjects()
  const workstreams = useWorkstreams()
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [account, setAccount] = useState('claude')
  const [draft, setDraft] = useState<ResetUsagePolicy | null>(null)
  const [busy, setBusy] = useState(false)
  const policy =
    draft ??
    ResetUsagePolicy.parse(
      settings.data?.usage?.[account] ?? settings.data?.usage?.[account.split(':')[0] ?? ''] ?? {},
    )
  const windows = accountWindows(usage.data ?? [], account)
  const patch = (p: Partial<ResetUsagePolicy>) => setDraft({ ...policy, ...p })
  const accounts = [
    ...new Set([
      'claude',
      'codex',
      ...(usage.data ?? []).map((u) => u.accountId).filter((id): id is string => !!id),
    ]),
  ]
  return (
    <section
      id="usage-settings"
      aria-labelledby="usage-settings-title"
      className="grid gap-3 px-4 pt-6"
    >
      <h2 id="usage-settings-title" className="text-[15px] font-semibold">
        Settings · Usage
      </h2>
      <p className="text-[14px] text-text-2">
        Use capacity before it resets. Suggestions need your approval; automatic runs require an
        existing standing grant. Only ready work and approved follow-ups are eligible.
      </p>
      <label className="field">
        <span>Provider account</span>
        <select
          className="input"
          value={account}
          onChange={(e) => {
            setAccount(e.target.value)
            setDraft(null)
          }}
        >
          {accounts.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
      </label>
      <div
        aria-label="Reported usage for this account"
        role="group"
        className="grid gap-1 text-[14px]"
      >
        <span className="text-text-2">
          Subscription headroom (quota windows the provider reported, not a charge)
        </span>
        {usage.isError ? (
          <span className="text-text-3">Usage is unavailable right now.</span>
        ) : windows.length ? (
          windows.map((w) => (
            <span key={w.id} data-headroom-window={w.id} className="text-text-1 tabular-nums">
              {w.label}: {w.limitReached ? 'exhausted' : `${Math.round(w.remainingPct)}% headroom`}
              {' · resets '}
              {new Date(w.resetsAt).toLocaleString([], {
                weekday: 'short',
                hour: 'numeric',
                minute: '2-digit',
              })}
              {w.stale ? (
                <span className="text-status-wait"> · stale, last reading may be old</span>
              ) : null}
            </span>
          ))
        ) : (
          <span className="text-text-3">
            No usage reported for this account yet; headroom is unknown, not zero.
          </span>
        )}
      </div>
      <label className="field">
        <span>Before a reset</span>
        <select
          className="input"
          value={policy.mode}
          onChange={(e) => patch({ mode: e.target.value as ResetUsagePolicy['mode'] })}
        >
          <option value="suggest">Suggest work</option>
          <option value="auto">Automatically use approved capacity</option>
          <option value="off">Off</option>
        </select>
      </label>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="field">
          <span>Reserve (%)</span>
          <input
            className="input"
            type="number"
            min="0"
            max="100"
            value={policy.reservePct}
            onChange={(e) => patch({ reservePct: Number(e.target.value) })}
          />
        </label>
        <label className="field">
          <span>Hours before reset</span>
          <input
            className="input"
            type="number"
            min="0.25"
            max="168"
            step="0.25"
            value={policy.horizonHours}
            onChange={(e) => patch({ horizonHours: Number(e.target.value) })}
          />
        </label>
        <label className="field">
          <span>Extra concurrent runs</span>
          <input
            className="input"
            type="number"
            min="0"
            max="20"
            value={policy.maxExtraConcurrent}
            onChange={(e) => patch({ maxExtraConcurrent: Number(e.target.value) })}
          />
        </label>
      </div>
      <fieldset className="grid gap-2">
        <legend>Projects (none selected means all authorized projects)</legend>
        {projects.data?.map((p) => (
          <label key={p.id} className="flex min-h-11 items-center gap-2">
            <input
              type="checkbox"
              checked={policy.projectIds.includes(p.id)}
              onChange={(e) =>
                patch({
                  projectIds: e.target.checked
                    ? [...policy.projectIds, p.id]
                    : policy.projectIds.filter((id) => id !== p.id),
                })
              }
            />
            {p.name}
          </label>
        ))}
      </fieldset>
      <fieldset className="grid gap-2">
        <legend>Workstreams (none selected means all authorized workstreams)</legend>
        {workstreams.data
          ?.filter((w) => !policy.projectIds.length || policy.projectIds.includes(w.projectId))
          .map((w) => (
            <label key={w.id} className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={policy.workstreamIds.includes(w.id)}
                onChange={(e) =>
                  patch({
                    workstreamIds: e.target.checked
                      ? [...policy.workstreamIds, w.id]
                      : policy.workstreamIds.filter((id) => id !== w.id),
                  })
                }
              />
              {w.name}
            </label>
          ))}
      </fieldset>
      <Button
        disabled={busy || !settings.data || !ResetUsagePolicy.safeParse(policy).success}
        onClick={() => {
          if (!settings.data) return
          setBusy(true)
          void b.api
            .saveSettings({
              ...settings.data,
              usage: { ...settings.data.usage, [account]: policy },
            })
            .then((saved) => {
              qc.setQueryData(qk.settings, saved)
              setDraft(null)
              toast('Usage settings saved')
            })
            .catch((e) => toast(errorText(e), 'error'))
            .finally(() => setBusy(false))
        }}
      >
        Save usage settings
      </Button>
    </section>
  )
}
